import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Calendar, User, Share2 } from 'lucide-react';
import { publicAPI } from '../services/apiService';
import { createSlug, extractIdFromSlug } from '../utils/slugify';
import { getImageUrl } from '../utils/imageUtils';

const BlogPost = () => {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPost();
    fetchRelatedPosts();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const postId = extractIdFromSlug(slug);
      const response = await publicAPI.getBlogPost(postId);
      setPost(response.data);
    } catch (error) {
      console.error('Error fetching blog post:', error);
      setError('Post not found');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRelatedPosts = async () => {
    try {
      const response = await publicAPI.getBlog();
      // Filter out current post and limit to 2 related posts
      const postId = extractIdFromSlug(slug);
      const filtered = response.data.filter(p => p.id !== postId).slice(0, 2);
      setRelatedPosts(filtered);
    } catch (error) {
      console.error('Error fetching related posts:', error);
    }
  };

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: currentLang === 'ru' ? post.title_ru : post.title_de,
          text: currentLang === 'ru' ? post.excerpt_ru : post.excerpt_de,
          url: window.location.href
        });
      } else {
        // Fallback - копируем URL в буфер обмена
        await navigator.clipboard.writeText(window.location.href);
        alert(currentLang === 'ru' ? 'Ссылка скопирована в буфер обмена!' : 'Link in die Zwischenablage kopiert!');
      }
    } catch (error) {
      // Игнорируем ошибку если пользователь отменил действие
      if (error.name === 'AbortError') {
        // Пользователь отменил - это нормально, ничего не делаем
        return;
      }
      // Только логируем другие ошибки, не показываем пользователю
      console.log('Share error:', error.message);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-black text-white min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-white"></div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="bg-black text-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">
            {currentLang === 'ru' ? 'Статья не найдена' : 'Artikel nicht gefunden'}
          </h1>
          <Link to="/blog" className="text-gray-400 hover:text-white">
            {currentLang === 'ru' ? 'Вернуться к блогу' : 'Zurück zum Blog'}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-black text-white">
      {/* Hero Section */}
      <section className="relative pt-24 pb-20">
        <div className="absolute inset-0 bg-black opacity-60 z-10"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('${getImageUrl(post.image)}')`
          }}
        ></div>
        <div className="relative z-20 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/blog"
            className="inline-flex items-center text-gray-300 hover:text-white mb-8 transition-colors duration-300"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {currentLang === 'ru' ? 'Вернуться к блогу' : 'Zurück zum Blog'}
          </Link>
          
          <div className="flex items-center gap-4 text-sm text-gray-300 mb-6">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <span>{new Date(post.created_at).toLocaleDateString()}</span>
            </div>
            <div className="flex items-center gap-2">
              <User className="h-4 w-4" />
              <span>{post.author}</span>
            </div>
          </div>
          
          <h1 
            className="text-4xl md:text-6xl font-bold mb-6 leading-tight"
            style={{ fontFamily: '"Times New Roman", serif' }}
          >
            {currentLang === 'ru' ? post.title_ru : post.title_de}
          </h1>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg prose-invert max-w-none">
            <div className="text-xl text-gray-300 leading-relaxed mb-8">
              {currentLang === 'ru' ? post.excerpt_ru : post.excerpt_de}
            </div>
            
            <div className="text-gray-300 leading-relaxed space-y-6">
              {(currentLang === 'ru' ? post.content_ru : post.content_de)?.split('\n').map((paragraph, index) => (
                <p key={index} className="text-lg leading-relaxed">
                  {paragraph}
                </p>
              ))}
            </div>
          </div>

          {/* Share Section */}
          <div className="mt-12 pt-8 border-t border-gray-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="text-gray-400">
                  {currentLang === 'ru' ? 'Поделиться:' : 'Artikel teilen:'}
                </span>
                <button 
                  onClick={handleShare}
                  className="p-2 border border-gray-600 hover:border-white hover:text-white transition-all duration-300"
                >
                  <Share2 className="h-4 w-4" />
                </button>
              </div>
              <Link 
                to="/blog"
                className="text-gray-400 hover:text-white transition-colors duration-300"
              >
                {currentLang === 'ru' ? 'Все статьи' : 'Alle Artikel anzeigen'}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 
            className="text-3xl font-bold mb-12 text-center"
            style={{ fontFamily: '"Times New Roman", serif' }}
          >
            {currentLang === 'ru' ? 'Другие статьи' : 'Weitere Artikel'}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {relatedPosts.map((relatedPost) => (
              <Link 
                key={relatedPost.id} 
                to={`/blog/${createSlug(relatedPost.title_de)}-${relatedPost.id}`}
                className="group bg-black overflow-hidden hover:bg-gray-800 transition-all duration-300"
              >
                <div className="aspect-w-16 aspect-h-9 overflow-hidden">
                  <img 
                    src={getImageUrl(relatedPost.image)} 
                    alt={currentLang === 'ru' ? relatedPost.title_ru : relatedPost.title_de}
                    className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="text-sm text-gray-400 mb-2">
                    {new Date(relatedPost.created_at).toLocaleDateString()}
                  </div>
                  <h3 
                    className="text-xl font-semibold mb-3 group-hover:text-gray-300 transition-colors duration-300"
                    style={{ fontFamily: '"Times New Roman", serif' }}
                  >
                    {currentLang === 'ru' ? relatedPost.title_ru : relatedPost.title_de}
                  </h3>
                  <p className="text-gray-400 text-sm">
                    {currentLang === 'ru' ? relatedPost.excerpt_ru : relatedPost.excerpt_de}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;